import { apiClient } from './client'

// Types
export interface MealType {
  _id: string
  institutionId: string
  name: string
  description?: string
  defaultTime: string
  duration: number
  price: number
  targetType: 'all' | 'grades' | 'streams'
  targetGrades?: string[]
  targetStreams?: string[]
  currentSessionId?: string
  isRunning: boolean
  isActive: boolean
  sortOrder: number
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface CreateMealTypeRequest {
  name: string
  description?: string
  defaultTime: string
  duration?: number
  price?: number
  sortOrder?: number
  targetType?: 'all' | 'grades' | 'streams'
  targetGrades?: string[]
  targetStreams?: string[]
  institutionId?: string
}

export interface UpdateMealTypeRequest {
  name?: string
  description?: string
  defaultTime?: string
  duration?: number
  price?: number
  isActive?: boolean
  sortOrder?: number
  targetType?: 'all' | 'grades' | 'streams'
  targetGrades?: string[]
  targetStreams?: string[]
}

export interface MealSession {
  _id: string
  institutionId: string
  mealTypeId: string | MealType
  menuId?: string
  sessionDate: string
  startTime: string
  endTime: string
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled'
  targetType: 'all' | 'grades' | 'streams'
  targetGrades?: string[]
  targetStreams?: string[]
  expectedCount: number
  actualCount: number
  location?: string
  supervisorId?: string | { _id: string; firstName: string; lastName: string }
  notes?: string
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface CreateMealSessionRequest {
  mealTypeId: string
  menuId?: string
  sessionDate: string
  startTime?: string
  endTime?: string
  targetType?: 'all' | 'grades' | 'streams'
  targetGrades?: string[]
  targetStreams?: string[]
  location?: string
  supervisorId?: string
  notes?: string
  institutionId?: string
}

export interface UpdateMealSessionRequest {
  mealTypeId?: string
  menuId?: string
  sessionDate?: string
  startTime?: string
  endTime?: string
  status?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled'
  targetType?: 'all' | 'grades' | 'streams'
  targetGrades?: string[]
  targetStreams?: string[]
  location?: string
  supervisorId?: string
  notes?: string
}

export interface MealAttendance {
  _id: string
  institutionId: string
  sessionId: string
  studentId: string | {
    _id: string
    firstName: string
    lastName: string
    studentId?: string
  }
  status: 'present' | 'absent' | 'excused'
  checkInTime?: string
  checkInMethod: 'manual' | 'rfid' | 'qr_code'
  portion: 'full' | 'half' | 'none'
  specialRequirements?: string
  notes?: string
  recordedBy: string
  createdAt: string
  updatedAt: string
}

export interface RecordAttendanceRequest {
  studentId: string
  status?: 'present' | 'absent' | 'excused'
  checkInMethod?: 'manual' | 'rfid' | 'qr_code'
  portion?: 'full' | 'half' | 'none'
  specialRequirements?: string
  notes?: string
}

export interface MealStats {
  totalSessions: number
  completedSessions: number
  totalMealTypes: number
  byStatus: Record<string, number>
  attendance: {
    totalExpected: number
    totalActual: number
    rate: string
  }
}

export interface Institution {
  _id: string
  name: string
  slug: string
}

// API Response types
interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

interface PaginatedResponse<T> {
  success: boolean
  data: {
    sessions: T[]
    pagination: {
      currentPage: number
      totalPages: number
      totalItems: number
      limit: number
    }
  }
}

// ============================================================================
// MEAL TYPES API
// ============================================================================

export const getMealTypes = async (params?: {
  isActive?: boolean
  search?: string
  institutionId?: string
}): Promise<ApiResponse<MealType[]>> => {
  const response = await apiClient.get('/meals/types', { params })
  return response.data
}

export const getMealType = async (id: string): Promise<ApiResponse<MealType>> => {
  const response = await apiClient.get(`/meals/types/${id}`)
  return response.data
}

export const createMealType = async (data: CreateMealTypeRequest): Promise<ApiResponse<MealType>> => {
  const response = await apiClient.post('/meals/types', data)
  return response.data
}

export const updateMealType = async (id: string, data: UpdateMealTypeRequest): Promise<ApiResponse<MealType>> => {
  const response = await apiClient.put(`/meals/types/${id}`, data)
  return response.data
}

export const deleteMealType = async (id: string): Promise<ApiResponse<null>> => {
  const response = await apiClient.delete(`/meals/types/${id}`)
  return response.data
}

export const startMealType = async (id: string): Promise<ApiResponse<{ mealType: MealType; session: MealSession }>> => {
  const response = await apiClient.post(`/meals/types/${id}/start`)
  return response.data
}

export const stopMealType = async (id: string): Promise<ApiResponse<MealType>> => {
  const response = await apiClient.post(`/meals/types/${id}/stop`)
  return response.data
}

// ============================================================================
// MEAL SESSIONS API
// ============================================================================

export const getMealSessions = async (params?: {
  status?: string
  mealTypeId?: string
  date?: string
  startDate?: string
  endDate?: string
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  institutionId?: string
}): Promise<PaginatedResponse<MealSession>> => {
  const response = await apiClient.get('/meals/sessions', { params })
  return response.data
}

export const getMealSession = async (id: string): Promise<ApiResponse<MealSession & { attendance: MealAttendance[] }>> => {
  const response = await apiClient.get(`/meals/sessions/${id}`)
  return response.data
}

export const createMealSession = async (data: CreateMealSessionRequest): Promise<ApiResponse<MealSession>> => {
  const response = await apiClient.post('/meals/sessions', data)
  return response.data
}

export const updateMealSession = async (id: string, data: UpdateMealSessionRequest): Promise<ApiResponse<MealSession>> => {
  const response = await apiClient.put(`/meals/sessions/${id}`, data)
  return response.data
}

export const deleteMealSession = async (id: string): Promise<ApiResponse<null>> => {
  const response = await apiClient.delete(`/meals/sessions/${id}`)
  return response.data
}

export const startMealSession = async (id: string): Promise<ApiResponse<MealSession>> => {
  const response = await apiClient.post(`/meals/sessions/${id}/start`)
  return response.data
}

export const endMealSession = async (id: string): Promise<ApiResponse<MealSession>> => {
  const response = await apiClient.post(`/meals/sessions/${id}/end`)
  return response.data
}

// ============================================================================
// MEAL ATTENDANCE API
// ============================================================================

export const getSessionAttendance = async (sessionId: string): Promise<ApiResponse<{
  attendance: MealAttendance[]
  stats: {
    total: number
    present: number
    absent: number
    excused: number
  }
}>> => {
  const response = await apiClient.get(`/meals/sessions/${sessionId}/attendance`)
  return response.data
}

export const recordAttendance = async (sessionId: string, data: RecordAttendanceRequest): Promise<ApiResponse<MealAttendance>> => {
  const response = await apiClient.post(`/meals/sessions/${sessionId}/attendance`, data)
  return response.data
}

export const bulkRecordAttendance = async (sessionId: string, attendanceRecords: RecordAttendanceRequest[]): Promise<ApiResponse<{
  created: number
  updated: number
  errors: string[]
}>> => {
  const response = await apiClient.post(`/meals/sessions/${sessionId}/attendance/bulk`, { attendanceRecords })
  return response.data
}

// ============================================================================
// STATS & UTILITIES API
// ============================================================================

export const getMealStats = async (params?: {
  startDate?: string
  endDate?: string
  institutionId?: string
}): Promise<ApiResponse<MealStats>> => {
  const response = await apiClient.get('/meals/stats', { params })
  return response.data
}

export const getInstitutionsForMeals = async (): Promise<ApiResponse<Institution[]>> => {
  const response = await apiClient.get('/meals/institutions')
  return response.data
}

// ============================================================================
// ANALYTICS & REPORTS API
// ============================================================================

export interface MealAnalytics {
  overview: {
    totalSessions: number
    completedSessions: number
    inProgressSessions: number
    cancelledSessions: number
    totalMealTypes: number
    activeMealTypes: number
    attendanceRate: string
    weeklyGrowth: string
    totalMealsServed: number
    avgMealsPerSession: number
  }
  attendance: {
    totalExpected: number
    totalActual: number
    rate: string
  }
  charts: {
    sessionsByMealType: Array<{
      _id: string
      name: string
      count: number
      totalExpected: number
      totalActual: number
      attendanceRate: number
    }>
    dailyTrends: Array<{
      date: string
      sessions: number
      expected: number
      actual: number
      rate: number
    }>
    attendanceByMealType: Array<{
      _id: string
      count: number
    }>
    peakHours: Array<{
      _id: string
      sessions: number
      avgAttendance: number
    }>
    portionDistribution: Array<{
      name: string
      value: number
    }>
    checkInMethods: Array<{
      name: string
      value: number
    }>
  }
  topMealTypes: Array<{
    _id: string
    name: string
    sessions: number
    totalExpected: number
    totalActual: number
    attendanceRate: number
  }>
  recentSessions: Array<{
    _id: string
    mealTypeName: string
    sessionDate: string
    startTime: string
    endTime: string
    status: string
    expectedCount: number
    actualCount: number
    attendanceRate: string
  }>
}

export interface MealReportExport {
  generatedAt: string
  period: {
    startDate: string
    endDate: string
  }
  summary: {
    totalSessions: number
    totalExpected: number
    totalActual: number
    overallAttendanceRate: string
    totalMealTypes: number
  }
  byMealType: Array<{
    name: string
    sessions: number
    expected: number
    actual: number
    attendanceRate: string
    estimatedRevenue: number
  }>
  sessions: Array<{
    date: string
    mealType: string
    startTime: string
    endTime: string
    expected: number
    actual: number
    attendanceRate: string
  }>
}

export interface StudentMealHistory {
  attendance: MealAttendance[]
  stats: {
    totalMeals: number
    present: number
    absent: number
    excused: number
  }
  pagination: {
    currentPage: number
    totalPages: number
    totalItems: number
    limit: number
  }
}

export const getMealAnalytics = async (params?: {
  startDate?: string
  endDate?: string
  mealTypeId?: string
  institutionId?: string
}): Promise<ApiResponse<MealAnalytics>> => {
  const response = await apiClient.get('/meals/analytics', { params })
  return response.data
}

export const exportMealReport = async (params?: {
  startDate?: string
  endDate?: string
  mealTypeId?: string
  institutionId?: string
}): Promise<ApiResponse<MealReportExport>> => {
  const response = await apiClient.get('/meals/reports/export', { params })
  return response.data
}

export const getStudentMealHistory = async (
  studentId: string,
  params?: {
    startDate?: string
    endDate?: string
    page?: number
    limit?: number
  }
): Promise<ApiResponse<StudentMealHistory>> => {
  const response = await apiClient.get(`/meals/students/${studentId}/history`, { params })
  return response.data
}

// ============================================================================
// STUDENT LOOKUP API (for attendance)
// ============================================================================

export interface StudentLookupResult {
  _id: string
  firstName: string
  lastName: string
  email: string
  studentId?: string
  gradeId?: string
  streamId?: string
  grade?: {
    name: string
  }
  stream?: {
    name: string
  }
  profileImage?: string
}

export const lookupStudentByRfid = async (rfidOrCardNumber: string): Promise<ApiResponse<StudentLookupResult | null>> => {
  const response = await apiClient.get(`/rfid/cards/lookup`, { params: { search: rfidOrCardNumber } })
  return response.data
}

export const searchStudents = async (params: {
  search?: string
  gradeId?: string
  streamId?: string
  limit?: number
}): Promise<ApiResponse<StudentLookupResult[]>> => {
  const response = await apiClient.get('/meals/students/search', { params })
  return response.data
}
